import * as THREE from "three";
import { tokens } from "../brand/tokens";
import type { QualityPreset } from "../systems/quality";
import { CanopyField } from "./canopy";
import { ChapterDirector } from "./chapterDirector";
import { LettermarkField } from "./lettermark";
import { VaporField } from "./vapor";
import { TopicChambers } from "./chambers";

export class WorldScene {
  readonly scene = new THREE.Scene();
  readonly camera: THREE.PerspectiveCamera;
  readonly renderer: THREE.WebGLRenderer;
  readonly director = new ChapterDirector();
  readonly canopy: CanopyField;
  readonly lettermark: LettermarkField;
  readonly vapor: VaporField;
  readonly chambers: TopicChambers;

  private keyLight: THREE.DirectionalLight;
  private rimLight: THREE.DirectionalLight;
  private portal: THREE.Mesh;
  private quality: QualityPreset;
  private running = true;
  private raf = 0;
  private last = performance.now();
  private progress = 0;

  constructor(canvas: HTMLCanvasElement, quality: QualityPreset) {
    this.quality = quality;
    this.camera = new THREE.PerspectiveCamera(42, 1, 0.1, 100);
    this.camera.position.set(0, 0.2, 6.5);

    this.renderer = new THREE.WebGLRenderer({
      canvas,
      antialias: quality.tier === "A",
      alpha: false,
      powerPreference: quality.tier === "A" ? "high-performance" : "default",
    });
    this.renderer.setClearColor(tokens.ink, 1);
    this.renderer.outputColorSpace = THREE.SRGBColorSpace;
    this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
    this.renderer.toneMappingExposure = 1.05;

    this.scene.fog = new THREE.FogExp2(tokens.ink, 0.045);
    this.scene.background = new THREE.Color(tokens.ink);

    const amb = new THREE.AmbientLight(0x1a2420, 0.55);
    this.keyLight = new THREE.DirectionalLight(0xdce5de, 1.1);
    this.keyLight.position.set(2.5, 4.5, 3.5);
    this.rimLight = new THREE.DirectionalLight(tokens.leaf, 0.5);
    this.rimLight.position.set(-3, 1.5, -2);

    this.scene.add(amb, this.keyLight, this.rimLight);

    this.canopy = new CanopyField(quality);
    this.lettermark = new LettermarkField(quality);
    this.vapor = new VaporField(quality);
    this.chambers = new TopicChambers(quality);
    this.portal = this.buildPortal();

    this.scene.add(
      this.canopy.group,
      this.lettermark.group,
      this.vapor.group,
      this.chambers.group,
      this.portal,
    );

    this.resize();
    window.addEventListener("resize", this.resize);
    document.addEventListener("visibilitychange", this.onVisibility);
  }

  private buildPortal() {
    const geo = new THREE.TorusGeometry(0.55, 0.035, 16, 64);
    const mat = new THREE.MeshStandardMaterial({
      color: tokens.leaf,
      emissive: tokens.leaf,
      emissiveIntensity: 0.2,
      roughness: 0.3,
      metalness: 0.4,
    });
    const mesh = new THREE.Mesh(geo, mat);
    mesh.position.set(0, 0.05, -0.2);
    mesh.rotation.x = Math.PI * 0.5;
    mesh.scale.setScalar(0.01);
    return mesh;
  }

  setProgress(progress: number) {
    this.progress = progress;
    this.director.applyCamera(this.camera, progress, this.quality.reducedMotion);
    this.director.applyLights(this.keyLight, this.rimLight, progress);
    this.canopy.setProgress(progress);
    this.chambers.setProgress(progress);

    const crystalT = Math.max(0, Math.min(1, (progress - 0.04) / 0.2));
    this.lettermark.setCrystallize(crystalT);
    this.lettermark.group.visible = crystalT > 0.01;
    this.canopy.group.visible = progress > 0.12;

    const portalT = Math.max(0, (progress - 0.82) / 0.18);
    this.portal.scale.setScalar(0.01 + portalT * 0.99);
    (this.portal.material as THREE.MeshStandardMaterial).emissiveIntensity =
      0.2 + portalT * 1.2;
  }

  start() {
    this.last = performance.now();
    this.running = true;
    cancelAnimationFrame(this.raf);
    if (this.quality.reducedMotion) {
      this.setProgress(this.progress);
      this.renderer.render(this.scene, this.camera);
      return;
    }
    const loop = (now: number) => {
      if (!this.running) return;
      const dt = Math.min(0.033, (now - this.last) / 1000);
      this.last = now;
      this.canopy.update(dt);
      this.lettermark.update(dt);
      this.vapor.update(dt);
      this.chambers.update(dt, this.progress);
      this.renderer.render(this.scene, this.camera);
      this.raf = requestAnimationFrame(loop);
    };
    this.raf = requestAnimationFrame(loop);
  }

  private resize = () => {
    const w = window.innerWidth;
    const h = window.innerHeight;
    const dpr = Math.min(window.devicePixelRatio || 1, this.quality.dprCap);
    this.camera.aspect = w / h;
    this.camera.updateProjectionMatrix();
    this.renderer.setPixelRatio(dpr);
    this.renderer.setSize(w, h, false);
  };

  private onVisibility = () => {
    this.running = !document.hidden;
    if (this.running) this.start();
  };

  dispose() {
    cancelAnimationFrame(this.raf);
    window.removeEventListener("resize", this.resize);
    document.removeEventListener("visibilitychange", this.onVisibility);
    this.canopy.dispose();
    this.lettermark.dispose();
    this.vapor.dispose();
    this.renderer.dispose();
  }
}

/** Lightweight background for curriculum / Club cannac view. */
export class CurriculumScene {
  readonly renderer: THREE.WebGLRenderer;
  readonly scene = new THREE.Scene();
  readonly camera: THREE.PerspectiveCamera;
  private canopy: CanopyField;
  private raf = 0;
  private running = true;
  private rim: THREE.DirectionalLight;

  constructor(canvas: HTMLCanvasElement, quality: QualityPreset) {
    this.camera = new THREE.PerspectiveCamera(50, 1, 0.1, 50);
    this.camera.position.set(0, 0.2, 3.6);
    this.renderer = new THREE.WebGLRenderer({
      canvas,
      antialias: false,
      alpha: true,
    });
    this.renderer.setClearColor(0x000000, 0);
    this.scene.add(new THREE.AmbientLight(0x1a3428, 0.75));
    this.rim = new THREE.DirectionalLight(tokens.leaf, 0.55);
    this.rim.position.set(2, 2.4, 2);
    this.scene.add(this.rim);
    const brassFill = new THREE.PointLight(0xc9a227, 0.35, 12);
    brassFill.position.set(-2, 1, 2);
    this.scene.add(brassFill);

    const q = { ...quality, particleScale: quality.particleScale * 0.55 };
    this.canopy = new CanopyField(q);
    this.canopy.group.scale.setScalar(1.25);
    this.canopy.group.position.y = -0.15;
    this.scene.add(this.canopy.group);
    this.resize();
    window.addEventListener("resize", this.resize);
  }

  /** Soft chapter tint for the club stage light */
  setChapterTint(hex: string | null) {
    try {
      this.rim.color.set(hex || tokens.leaf);
    } catch {
      this.rim.color.set(tokens.leaf);
    }
  }

  start() {
    const loop = (now: number) => {
      if (!this.running) return;
      this.canopy.update(0.016);
      this.canopy.group.rotation.y = now * 0.00007;
      this.canopy.group.rotation.x = Math.sin(now * 0.0002) * 0.04;
      this.renderer.render(this.scene, this.camera);
      this.raf = requestAnimationFrame(loop);
    };
    this.raf = requestAnimationFrame(loop);
  }

  private resize = () => {
    const w = window.innerWidth;
    const h = window.innerHeight;
    this.camera.aspect = w / h;
    this.camera.updateProjectionMatrix();
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 1.5));
    this.renderer.setSize(w, h, false);
  };

  dispose() {
    this.running = false;
    cancelAnimationFrame(this.raf);
    window.removeEventListener("resize", this.resize);
    this.canopy.dispose();
    this.renderer.dispose();
  }
}
